package com.milta.feature.admin.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.admin.domain.usecase.GetAdminAccountsUseCase
import kotlinx.coroutines.launch

/**
 * ViewModel for the Admin "Utilisateurs" screen (2026-10-03) — web-only, read-only: no
 * create/update/delete intent exists on purpose. A single [GetAdminAccountsUseCase] call returns
 * the whole cross-tenant list already grouped by organisation (see backend
 * GetAdminAccountsHandler) — search/collapse are pure client-side state over that one payload, no
 * further network round-trip.
 */
class AdminUsersViewModel(
    private val getAdminAccounts: GetAdminAccountsUseCase,
) : MviViewModel<AdminUsersState, AdminUsersIntent>(AdminUsersState()) {

    override fun onIntent(intent: AdminUsersIntent) {
        when (intent) {
            AdminUsersIntent.Load -> load()
            is AdminUsersIntent.UpdateSearch -> setState { copy(searchQuery = intent.query) }
            is AdminUsersIntent.ToggleOrganisation -> setState {
                val ids = expandedOrganisationIds.toMutableSet()
                if (!ids.add(intent.enterpriseId)) ids.remove(intent.enterpriseId)
                copy(expandedOrganisationIds = ids)
            }
            AdminUsersIntent.ExpandAll -> setState {
                copy(expandedOrganisationIds = data?.organisations.orEmpty().map { it.enterpriseId }.toSet())
            }
            AdminUsersIntent.CollapseAll -> setState { copy(expandedOrganisationIds = emptySet()) }
        }
    }

    private fun load() {
        scope.launch {
            setState { copy(isLoading = true, error = null) }
            getAdminAccounts().fold(
                onSuccess = { accounts -> setState { copy(isLoading = false, data = accounts) } },
                onFailure = { e ->
                    val errorCode = if (e is ApiException) "server_error" else "offline"
                    setState { copy(isLoading = false, error = errorCode) }
                },
            )
        }
    }
}
